import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from "@angular/material/snack-bar";
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { Menu, Permission, Role, User } from '../sidebar/sidebar-item/menu-types';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

const BACKEND_URL = environment.webapiURL;

type Response<T> = {
  result: T,
  success: boolean
}

@Injectable({
  providedIn: 'root'
})
export class RoleService {

  constructor(
    private http: HttpClient, 
    private authService: AuthService,
    private _snackBar: MatSnackBar,
    private translateService: TranslateService

  ) { }

  public async hasRole(role: string): Promise<boolean> {
    const userId = this.authService.getLoggedUser().userId;
    if (typeof userId !== 'number') return false;
    
    let hasRole = false;
    await this.http.get(`${BACKEND_URL}/roles/user?userId=${userId}`)
      .forEach((response: Response<[Role]>) => {
        response.result.forEach(userRole => {
          if (userRole.role_id == role)
            hasRole = true;
        })
      });

    return hasRole;
  }

  public getPermissionsByCurrentUser() {
      const userId = this.authService.getLoggedUser().userId;
      if (typeof userId !== 'number') return;
      return this.http.get<Response<Array<string>>>(`${BACKEND_URL}/roles/user/permissions?userId=${userId}`).pipe(
        catchError(err => {
          if (err.status === 403) {
           this.openSnackBar(this.translateService.instant(this.authService.t10s.LOGIN.LOGIN.invalidPermissions), "x");
            console.log("Permission denied");
          }
          return of ({result: [], success: false});
        })
      );
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, "x", {
      duration: 2000,
      verticalPosition: "top",
    });
  }


  public getPermissionsByUser(userId: number){
    if (typeof userId !== 'number') return;
    return this.http.get<Response<Array<string>>>(`${BACKEND_URL}/roles/user/permissionsIds?userId=${userId}`);
  }

  public getMenusByCurrentUser() {
    const userId = this.authService.getLoggedUser().userId;
    if (typeof userId !== 'number') return;
    return this.http.get<Response<[Menu]>>(`${BACKEND_URL}/roles/menus?userId=${userId}`);
  }

  public getPbiMenus() {
    return this.http.get<Response<[Menu]>>(`${BACKEND_URL}/roles/pbi/menus`);
  }

  public getRolesByUser(userId: number) {
    return this.http.get<Response<[Role]>>(`${BACKEND_URL}/roles/user?userId=${userId}`);
  }

  public getPermissionsByRole(roleId: string) {
    return this.http.get<Response<[Permission]>>(`${BACKEND_URL}/roles/permissions/${roleId}`);
  }

  public getRoles() {
    return this.http.get<Response<[Role]>>(`${BACKEND_URL}/roles`);
  }

  public getPermissions() {
    return this.http.get<Response<[Permission]>>(`${BACKEND_URL}/roles/permissions`);
  }

  public getUsers() {
    return this.http.get<Response<[User]>>(`${BACKEND_URL}/roles/users`);
  }

  public getMenus() {
    return this.http.get<Response<[Menu]>>(`${BACKEND_URL}/roles/all/menus`);
  }

  public getMenu(menu: string) {
    return this.http.get<Response<Menu>>(`${BACKEND_URL}/roles/menus/${menu}`);
  }

  public saveUserRoles(userId: number, roles: Array<string>) {
    return this.http.post(`${BACKEND_URL}/roles/user/roles`, {user: userId, roles});
  }

  public createRole(role: string) {
    return this.http.post(`${BACKEND_URL}/roles`, { role });
  }

  public deleteRole(role: string) {
    return this.http.delete(`${BACKEND_URL}/roles/${role}`);
  }

  public createPermission(permission: string) {
    return this.http.post(`${BACKEND_URL}/roles/permission`, { permission });
  }

  public savePermissions(role: string, permissions: Array<Permission>) {
    return this.http.put(`${BACKEND_URL}/roles/permissions`, {role, permissions});
  }

  public savePermission(permission: string, menu: string) {
    return this.http.put(`${BACKEND_URL}/roles/permission`, {permission, menu});
  }

  public deletePermission(permission: string) {
    return this.http.delete<Response<Object>>(`${BACKEND_URL}/roles/permission/${permission}`);
  }

  public createMenu(menu: Partial<Menu>) {
    return this.http.post(`${BACKEND_URL}/roles/menu`, menu);
  }

  public updateMenu(menu: Partial<Menu>) {
    return this.http.put(`${BACKEND_URL}/roles/menu`, menu);
  }
  
  public deleteMenu(menu: string) {
    return this.http.delete<Response<Object>>(`${BACKEND_URL}/roles/menu/${menu}`);
  }

  public getUsersPerRoles() {
    return this.http.get<Response<{user: number, username: string, email: string, role: string}[]>>(`${BACKEND_URL}/roles/list/users`);
  }

  public getRolesPerPermission() {
    return this.http.get<Response<{role: string, permission: string}[]>>(`${BACKEND_URL}/roles/list/permissions`);
  }

  public savePermissionsUsers(userId: string, permissionsId: Array<String>) {
    return this.http.post(`${BACKEND_URL}/roles/user/permission`, {userId, permissionsId});
  }

}
